import React, { useState, useEffect } from 'react';
import { X, Calendar } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { estadisticasService, MinutosPorJornada } from '../../services/EstadisticasService';

interface ModalListaPartidosProps {
  isOpen: boolean;
  onClose: () => void;
  jugadorId: number;
  jugadorNombre: string;
  temporadaId: number;
}

/**
 * Modal pequeño que muestra una lista vertical de los partidos jugados
 * y los minutos disputados en cada uno.
 */
const ModalListaPartidos: React.FC<ModalListaPartidosProps> = ({
  isOpen,
  onClose,
  jugadorId,
  jugadorNombre,
  temporadaId
}) => {
  const [partidos, setPartidos] = useState<MinutosPorJornada[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && jugadorId && temporadaId) {
      cargarPartidos();
    }
  }, [isOpen, jugadorId, temporadaId]);

  const cargarPartidos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await estadisticasService.obtenerMinutosPorJornada(jugadorId, temporadaId);
      // Filtramos solo los partidos donde ha jugado al menos 1 minuto
      const partidosJugados = data.filter(p => p.minutosJugador > 0);
      // Ordenamos por jornada descendente (más reciente primero)
      partidosJugados.sort((a, b) => b.jornada - a.jornada);
      setPartidos(partidosJugados);
    } catch (err) {
      console.error('Error al cargar partidos:', err);
      setError('Error al cargar los partidos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col h-full max-h-[70vh]">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-secondary-700 mb-4">
          <div>
            <h2 className="text-lg font-bold text-white font-poppins">
              Partidos Jugados
            </h2>
            <p className="text-secondary-400 text-sm font-poppins">
              {jugadorNombre}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-secondary-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-secondary-400" />
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2"></div>
              <span className="text-secondary-400 text-sm font-poppins">Cargando...</span>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <p className="text-red-400 text-sm font-poppins mb-2">{error}</p>
              <button 
                onClick={cargarPartidos}
                className="text-primary-500 text-sm underline"
              >
                Reintentar
              </button>
            </div>
          ) : partidos.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-secondary-600 mx-auto mb-2" />
              <p className="text-secondary-400 text-sm font-poppins">
                No ha participado en ningún partido esta temporada
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {partidos.map((partido) => (
                <div 
                  key={partido.jornada} 
                  className="bg-secondary-800/50 rounded-lg p-3 flex items-center justify-between border border-secondary-700/50 hover:bg-secondary-800 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="bg-secondary-700/50 p-2 rounded-lg">
                      <Calendar className="w-3 h-3 text-primary-400" />
                    </div>
                    <div>
                      <span className="text-white font-medium font-poppins block text-sm">
                        Jornada {partido.jornada}
                      </span>
                      {partido.rival && (
                        <span className="text-secondary-400 text-xs font-poppins block mt-0.5">
                          vs {partido.rival}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className="text-emerald-400 font-bold font-poppins text-base block">
                      {partido.minutosJugador}'
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer con resumen */}
        {!loading && !error && partidos.length > 0 && (
          <div className="mt-4 pt-4 border-t border-secondary-700 flex justify-between items-center">
            <span className="text-secondary-400 text-sm font-poppins">Total partidos:</span>
            <span className="text-white font-bold font-poppins">{partidos.length}</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalListaPartidos;
